// The one-line embed: creates the host element and calls mount() with the app origin. No host globals are patched.
import { mount } from "./main.ts";
import { TOKENS } from "../theme.ts";

const tag = document.currentScript as HTMLScriptElement | null;
const APP = tag ? new URL(tag.src).origin : "";
const site = tag?.dataset.site ?? "", quiz = tag?.dataset.quiz ?? "";
const mode = tag?.dataset.mode === "popup" ? "popup" : "inline";

function accent(): string {
  const set = tag?.dataset.accent;
  if (set) return set;
  const el = document.querySelector("a, button:not([data-sorted])");
  if (!el) return "";
  const cs = getComputedStyle(el);
  for (const v of [cs.backgroundColor, cs.color]) {
    const m = v.match(/rgba?\(([\d.]+)[, ]+([\d.]+)[, ]+([\d.]+)(?:[,/ ]+([\d.]+))?/);
    if (!m) continue;
    const [r, g, b] = [+m[1], +m[2], +m[3]], a = m[4] === undefined ? 1 : +m[4];
    if (a === 0 || (r + g + b === 0) || (r === 255 && g === 255 && b === 255)) continue;
    return v;
  }
  return "";
}

async function mountInto(parent: HTMLElement): Promise<void> {
  const host = document.createElement("div");
  host.id = "sorted-quiz";
  host.dataset.slug = quiz;
  if (mode === "inline") host.dataset.mode = "inline"; // styles.ts: size to content, no viewport min-height
  const a = accent();
  if (a) host.style.cssText = `--s-accent:${a};--s-accent-hover:${a};--s-accent-fg:${TOKENS["color-on-primary"]}`;
  parent.appendChild(host);
  await mount(host, { apiBase: APP });
}

function popup(): void {
  const btn = document.createElement("button");
  btn.setAttribute("data-sorted", "1");
  btn.textContent = tag?.dataset.label || "Take the quiz";
  const a = accent() || TOKENS["color-primary"];
  btn.style.cssText = `position:fixed;right:16px;bottom:16px;z-index:2147483646;padding:12px 20px;border:0;border-radius:999px;background:${a};color:${TOKENS["color-on-primary"]};font:600 15px system-ui,sans-serif;cursor:pointer;box-shadow:0 4px 16px rgba(0,0,0,.25)`;
  const dlg = document.createElement("dialog");
  dlg.setAttribute("aria-label", tag?.dataset.label || "Quiz");
  const style = document.createElement("style");
  style.textContent = "dialog[data-sorted-dlg]{padding:0;border:0;overflow:auto;background:" + TOKENS["color-surface"] + ";margin:auto;width:100vw;height:100vh;max-width:100vw;max-height:100vh;border-radius:0}"
    + "@media (min-width:600px){dialog[data-sorted-dlg]{width:min(640px,calc(100vw - 48px));height:auto;max-height:calc(100vh - 48px);border-radius:12px}}"
    + "dialog[data-sorted-dlg]::backdrop{background:rgba(15,23,42,.45)}";
  dlg.setAttribute("data-sorted-dlg", "1");
  document.head.appendChild(style);
  const close = document.createElement("button");
  close.setAttribute("data-sorted", "1");
  close.setAttribute("aria-label", "Close");
  close.textContent = "Close";
  close.style.cssText = "display:block;min-width:44px;min-height:44px;margin:8px 8px 0 auto;padding:0 12px;border:0;background:none;cursor:pointer;font:500 14px system-ui,sans-serif;color:" + TOKENS["color-ink-soft"];
  const shut = () => { if (dlg.open) dlg.close(); };
  close.onclick = shut;
  dlg.addEventListener("click", (e) => { if (e.target === dlg) shut(); });
  dlg.addEventListener("close", () => {
    document.body.style.overflow = prevOverflow;
    btn.style.display = "";
    btn.focus();
  });
  dlg.setAttribute("tabindex", "-1");
  dlg.appendChild(close);
  document.body.append(btn, dlg);
  let mounted = false, prevOverflow = "";
  btn.onclick = () => {
    prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    btn.style.display = "none";
    dlg.showModal();
    dlg.focus();
    if (!mounted) { mounted = true; void mountInto(dlg); }
  };
}

async function run(): Promise<void> {
  if (!tag || !site || !quiz) return;
  try {
    const r = await fetch(`${APP}/api/embed/check?site=${encodeURIComponent(site)}&quiz=${encodeURIComponent(quiz)}`);
    if (!r.ok || !(await r.json()).ok) return;
  } catch { return; }
  if (mode === "popup") popup();
  else { const slot = document.createElement("div"); tag.after(slot); await mountInto(slot); }
}
void run();
